import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { type GeneratePlanInput, type PlanDraftView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { GanttChart } from '../../gantt/components/gantt-chart';
import { errorMessage } from '../../../lib/error-message';
import { apiFetch } from '../../../lib/http';
import { randomUuid } from '../../../lib/uuid';
import { useTrackedAiRun } from '../../ai/use-ai-run';
import { useAiRunConfirm } from '../../ai/components/ai-run-confirm';
import { planApi, planKeys } from '../api';
import { planChatStore } from '../chat-store';
import { draftToGantt, rangeToOffsets } from '../preview';
import { ClarifyStep } from '../../ai/components/clarify-step';
import { AssignSection } from './assign-section';
import { PlanItemRow } from './plan-item-row';
import { PlanWizard } from './plan-wizard';
import { DatePicker } from '../../../components/ui/date-picker';
import { displayDateTime } from '../../../lib/date';
import { todayIso } from '../../../lib/overdue';

interface PlanPanelProps {
  readonly projectId: string;
  /**
   * Mở sẵn đúng bản nháp này — đường vào từ danh sách ở tab Điều phối
   * (APM-ROLE-v).
   *
   * Bỏ trống thì giữ nguyên hành vi cũ: ưu tiên nháp đang chờ trả lời, rồi tới
   * nháp chờ duyệt.
   */
  readonly initialDraftId?: string;
}

/**
 * Sinh, review và áp dụng kế hoạch — APM-603, spec mục 9.
 *
 * Ba vùng: ô mô tả để sinh, danh sách hạng mục sửa được, và Gantt xem trước.
 * Nút "Áp dụng" là chỗ **duy nhất** biến nháp thành task thật (ADR-005) — mọi
 * thứ phía trên nó chỉ đụng vào bảng nháp.
 */
export function PlanPanel({ projectId, initialDraftId }: PlanPanelProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const track = useTrackedAiRun();
  const aiConfirm = useAiRunConfirm(projectId);
  /**
   * Mã dấu vết của từng nháp đang chờ trả lời — APM-1302.
   *
   * Vòng hỏi lại là hai lời gọi HTTP cách nhau vài phút (người dùng đọc câu
   * hỏi rồi gõ). Giữ mã ở đây để lượt trả lời tiếp tục **cùng** dấu vết, nên
   * bảng bên phải đọc được cả ba chặng: AI hỏi → mình trả lời → kết quả.
   */
  const [runIds, setRunIds] = useState<Record<string, string>>({});
  const [instruction, setInstruction] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [openId, setOpenId] = useState<string | null>(initialDraftId ?? null);

  const key = planKeys.list(projectId);
  const drafts = useQuery({ queryKey: key, queryFn: () => planApi.list(projectId) });
  const templates = useQuery({ queryKey: planKeys.templates(), queryFn: planApi.templates });
  const members = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () =>
      apiFetch<{ userId: string; displayName: string | null }[]>(`/projects/${projectId}/members`),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });
  const fail = (error: unknown) => toast.error(errorMessage(error, t));

  /** Endpoint sửa nháp trả về cả nháp — ghi thẳng vào cache, khỏi tải lại. */
  const absorb = (draft: PlanDraftView) => {
    queryClient.setQueryData<PlanDraftView[]>(key, (current) =>
      (current ?? []).map((item) => (item.id === draft.id ? draft : item)),
    );
  };

  /**
   * Trả lời vòng hỏi lại — APM-806.
   *
   * Cùng nháp, nên `absorb` là đủ: server trả về nháp đã có hạng mục và
   * `status: 'pending'`, và màn hình chuyển sang bước duyệt mà không tải lại.
   *
   * ⚠️ Khai **trước** `generate`: `generate` gắn chính mutation này làm đường
   * gửi câu trả lời cho bảng theo dõi bên phải (APM-UX-ai1).
   */
  const answer = useMutation({
    mutationFn: ({ draftId, answers }: { draftId: string; answers: Record<string, string> }) => {
      // Nháp mở lại ở tab khác thì không còn mã cũ trong state — sinh mã mới
      // và dấu vết bắt đầu từ lượt này. Mất phần đầu, nhưng không mất lượt.
      const runId = runIds[draftId] ?? randomUuid();

      return track(
        {
          skill: 'generate_plan',
          label: t('plan.generate'),
          runId,
          describe: (draft: PlanDraftView) => t('plan.draftReady', { count: draft.items.length }),
        },
        () => planApi.answer(draftId, answers, runId),
      );
    },
    onSuccess: absorb,
    onError: fail,
  });

  const generate = useMutation({
    mutationFn: (input: Omit<GeneratePlanInput, 'projectId'>) => {
      const runId = randomUuid();

      return track(
        {
          skill: 'generate_plan',
          label: t('plan.generate'),
          runId,
          describe: (draft: PlanDraftView) =>
            draft.status === 'clarifying'
              ? t('plan.clarifyPending')
              : t('plan.draftReady', { count: draft.items.length }),
          /*
            Chỗ trả lời **thứ hai**, ngay trên bảng theo dõi — APM-UX-ai1.

            Biểu mẫu dưới đây (`ClarifyStep` trong wizard) vẫn là chỗ chính. Nhưng
            wizard sống trong một hộp thoại: đóng nó đi, chuyển sang tab khác, hay
            chỉ là không cuộn xuống — thế là ba câu hỏi vừa trả tiền nằm đó không
            ai trả lời, trong khi bảng bên phải hiện chúng ra mà không có ô nhập
            nào. Gắn đường gửi vào đây thì bảng trả lời được luôn.
          */
          clarify: (draft: PlanDraftView) =>
            draft.status === 'clarifying' && draft.clarify !== null
              ? {
                  questions: draft.clarify.questions,
                  projectId,
                  submit: (answers: Record<string, string>) =>
                    // `onError` của mutation đã hiện toast; nuốt ở đây để lời hứa
                    // không thành một rejection không ai bắt.
                    answer.mutateAsync({ draftId: draft.id, answers }).then(
                      () => undefined,
                      () => undefined,
                    ),
                }
              : null,
        },
        () => planApi.generate({ projectId, ...input, runId }),
      ).then((draft) => {
        setRunIds((current) => ({ ...current, [draft.id]: runId }));
        return draft;
      });
    },
    onSuccess: (draft) => {
      void invalidate();
      setOpenId(draft.id);
    },
    onError: fail,
  });

  const refine = useMutation({
    mutationFn: ({ draftId, text }: { draftId: string; text: string }) =>
      track(
        {
          skill: 'generate_plan',
          label: t('plan.refineLabel'),
          describe: (draft: PlanDraftView) => t('plan.draftReady', { count: draft.items.length }),
        },
        (runId) => planApi.refine(draftId, text, runId),
      ),
    onSuccess: (draft) => {
      absorb(draft);
      setInstruction('');
    },
    onError: fail,
  });

  const updateItem = useMutation({
    mutationFn: ({
      draftId,
      itemId,
      patch,
    }: {
      draftId: string;
      itemId: string;
      patch: Record<string, unknown>;
    }) => planApi.updateItem(draftId, itemId, patch),
    onSuccess: absorb,
    onError: fail,
  });

  const addItem = useMutation({
    mutationFn: ({ draftId, parentId }: { draftId: string; parentId?: string }) =>
      planApi.addItem(draftId, {
        title: t('plan.newItemTitle'),
        ...(parentId ? { parentId } : {}),
      }),
    onSuccess: absorb,
    onError: fail,
  });

  const removeItem = useMutation({
    mutationFn: ({ draftId, itemId }: { draftId: string; itemId: string }) =>
      planApi.removeItem(draftId, itemId),
    onSuccess: absorb,
    onError: fail,
  });

  const discard = useMutation({
    mutationFn: (draftId: string) => planApi.discard(draftId),
    onSuccess: (draft) => {
      absorb(draft);
      setOpenId(null);
    },
    onError: fail,
  });

  const apply = useMutation({
    mutationFn: (draftId: string) => planApi.apply(draftId, startDate),
    onSuccess: (result) => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: ['board', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['gantt', projectId] });
      setOpenId(null);

      toast.success(t('plan.applied', { count: result.tasksCreated }));
      // Hạng mục bị đổi loại hay bỏ qua phải nói ra, không im lặng.
      for (const item of result.skipped) toast.warning(`${item.title}: ${item.reason}`);
    },
    onError: fail,
  });

  /**
   * Nháp đang chờ trả lời được ưu tiên hơn nháp chờ duyệt — APM-806.
   *
   * Người dùng đóng tab giữa vòng hỏi lại là chuyện thường; mở lại mà không
   * thấy câu hỏi thì họ sinh nháp mới, và lượt vừa trả tiền thành rác.
   *
   * ⚠️ Tính **trước** hai nhánh return sớm bên dưới: `useEffect` ngay sau đây
   * cần nó, và một hook nằm sau `return` là hook chạy có điều kiện.
   */
  const live = (drafts.data ?? []).filter(
    (draft) => draft.status === 'pending' || draft.status === 'clarifying',
  );
  const open =
    drafts.data?.find((draft) => draft.id === openId) ??
    live.find((draft) => draft.status === 'clarifying') ??
    live[0] ??
    null;

  /**
   * Báo cho sidebar biết đang xem nháp nào, để nó mở khung chat — APM-ROLE-x2.
   *
   * Dọn khi rời màn hình, và `close` nhận `draftId` để không đóng nhầm: hộp
   * thoại cũ có thể unmount **sau** khi hộp thoại mới đã mở.
   */
  const openDraftId = open?.id ?? null;
  useEffect(() => {
    if (openDraftId === null) return undefined;
    planChatStore.open({ projectId, draftId: openDraftId });
    return () => planChatStore.close(openDraftId);
  }, [projectId, openDraftId]);

  if (drafts.isPending) return <Skeleton className="h-64 w-full" />;
  if (drafts.isError) {
    return (
      <ErrorState message={errorMessage(drafts.error, t)} onRetry={() => void drafts.refetch()} />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PlanWizard
        projectId={projectId}
        templates={templates.data ?? []}
        pending={generate.isPending}
        onGenerate={(input) => generate.mutate(input)}
      />

      {open === null ? (
        <EmptyState title={t('plan.noDraft')} description={t('plan.noDraftHelp')} />
      ) : open.status === 'clarifying' && open.clarify !== null ? (
        <ClarifyStep
          clarify={open.clarify}
          pending={answer.isPending}
          onSubmit={(answers) => answer.mutate({ draftId: open.id, answers })}
          projectId={projectId}
          {...(runIds[open.id] ? { runId: runIds[open.id] } : {})}
        />
      ) : (
        <section className="flex flex-col gap-3">
          <header className="flex flex-wrap items-center gap-2">
            <Sparkles className="text-muted-foreground" />
            <span className="text-sm font-medium">
              {t('plan.draftTitle', { count: open.items.filter((i) => !i.excluded).length })}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('plan.totalHours', { hours: open.totalEstimateHours })} · ${open.costUsd}
            </span>

            {drafts.data.length > 1 ? (
              <select
                className="h-8 rounded-control border border-border-strong bg-background px-2 text-xs"
                aria-label={t('plan.chooseDraft')}
                value={open.id}
                onChange={(event) => setOpenId(event.target.value)}
              >
                {drafts.data.map((draft) => (
                  <option key={draft.id} value={draft.id}>
                    {displayDateTime(draft.createdAt)} · {t(`plan.status.${draft.status}`)}
                  </option>
                ))}
              </select>
            ) : null}
          </header>

          {open.rationale ? (
            <p className="rounded-DEFAULT bg-muted/40 p-2 text-xs">{open.rationale}</p>
          ) : null}

          {/*
            Chọn người — APM-UX-ai4. Đặt **trên** danh sách hạng mục: đọc xong
            lý do chia việc thì câu hỏi kế tiếp là ai làm, và câu trả lời phải ở
            ngay đó chứ không nằm sau sáu mươi dòng.

            Chỉ ở nháp `pending`: nháp đang chờ trả lời thì chưa có hạng mục nào
            để giao.
          */}
          {open.status === 'pending' ? (
            <AssignSection draft={open} members={members.data ?? []} startDate={startDate} />
          ) : null}

          <ul className="flex flex-col gap-2">
            {open.items
              .filter((item) => item.parentId === null)
              .map((item) => (
                <PlanItemRow
                  key={item.id}
                  draft={open}
                  item={item}
                  members={members.data ?? []}
                  onUpdate={(itemId, patch) =>
                    updateItem.mutate({ draftId: open.id, itemId, patch })
                  }
                  onSplit={(parentId) => addItem.mutate({ draftId: open.id, parentId })}
                  onRemove={(itemId) => removeItem.mutate({ draftId: open.id, itemId })}
                />
              ))}
          </ul>

          <Button
            size="sm"
            variant="outline"
            className="self-start"
            disabled={open.status !== 'pending'}
            onClick={() => addItem.mutate({ draftId: open.id })}
          >
            {t('plan.addItem')}
          </Button>

          <div>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-sm font-medium">{t('plan.previewTitle')}</h4>

              {/*
                Độ dài đường găng — APM-1314.

                Con số này KHÁC tổng giờ ngay bên trên, và khác theo hướng người
                ta hay nhầm: 400 giờ chia cho năm người song song không phải 400
                giờ trên trục thời gian. Nó là câu trả lời cho "kế hoạch này dài
                bao lâu", và nó phải đọc được **trước** khi bấm Áp dụng.
              */}
              {open.criticalPathDays > 0 ? (
                <span className="text-xs text-muted-foreground">
                  {t('gantt.criticalLength', { days: open.criticalPathDays })}
                </span>
              ) : null}
            </div>
            {/* Dùng lại đúng component Gantt của APM-503: hai bản vẽ khác nhau
                sẽ dần trông khác nhau, đúng lúc người dùng cần chúng giống nhau
                để tin vào bản xem trước. */}
            {/*
              Kéo thanh **sửa được** nháp — APM-ROLE-w.
              
              ⚠️ Và nó vẫn chỉ sửa nháp: `updateItem` gọi
              `PATCH /plans/:draftId/items/:id`, không đụng bảng `task`. Sửa tay
              không phải là duyệt — ADR-005 nói người bấm duyệt chịu trách nhiệm
              cho **từng** nước đi, và sửa một dòng không phải là đọc tám dòng
              còn lại.

              Nháp đã áp dụng hoặc đã bỏ thì khoá: sửa lịch của một nháp đã
              thành task thật là sửa một thứ không còn ai đọc.
            */}
            <GanttChart
              view={draftToGantt(open, new Date(`${startDate}T00:00:00Z`))}
              zoom="week"
              asOf={new Date(`${startDate}T00:00:00Z`)}
              onReschedule={(itemId, range) => {
                if (open.status !== 'pending') return;
                updateItem.mutate({
                  draftId: open.id,
                  itemId,
                  patch: rangeToOffsets(range, new Date(`${startDate}T00:00:00Z`)),
                });
              }}
            />
          </div>

          {open.refinements.length > 0 ? (
            <ol className="flex flex-col gap-1 text-xs text-muted-foreground">
              {open.refinements.map((entry) => (
                <li key={entry.at}>→ {entry.instruction}</li>
              ))}
            </ol>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <input
              className="h-8 min-w-60 flex-1 rounded-control border border-border-strong bg-background px-2 text-sm"
              placeholder={t('plan.refinePlaceholder')}
              aria-label={t('plan.refine')}
              value={instruction}
              disabled={open.status !== 'pending'}
              onChange={(event) => setInstruction(event.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={
                open.status !== 'pending' || instruction.trim().length < 3 || refine.isPending
              }
              onClick={() => {
                const input = { draftId: open.id, text: instruction.trim() };
                aiConfirm.confirm('generate_plan', t('plan.refine'), () => refine.mutate(input));
              }}
            >
              {refine.isPending ? t('plan.refining') : t('plan.refine')}
            </Button>
            {aiConfirm.dialog}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <label className="text-xs text-muted-foreground" htmlFor="plan-start">
              {t('plan.startDate')}
            </label>
            <DatePicker
              id="plan-start"
              className="w-40"
              value={startDate}
              onChange={setStartDate}
            />

            <Button
              disabled={open.status !== 'pending' || apply.isPending}
              onClick={() => apply.mutate(open.id)}
            >
              {t('plan.apply')}
            </Button>

            <Button
              variant="ghost"
              disabled={open.status !== 'pending'}
              onClick={() => discard.mutate(open.id)}
            >
              {t('plan.discard')}
            </Button>

            {open.status !== 'pending' ? (
              <span className="text-xs text-muted-foreground">
                {t(`plan.status.${open.status}`)}
              </span>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}
